import { CardContent, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { SEVERITY_COLORS } from '../../../../constants/map.constants';
import { ParameterRow, StatusSeverity } from '../../../../models/models';
import { Card } from './param-chart.styles';

const ParamChart = ({
    param,
    delta,
    chartData,
}: {
    param: ParameterRow;
    delta: number;
    chartData: { ts: number; value: number }[];
}) => {
    const theme = useTheme();

    const getColor = () => {
        const status = getStatus();
        return status != null
            ? SEVERITY_COLORS[status as StatusSeverity]
            : '#000';
    };

    const getFinalValue = (value: number) => {
        if (param.type === 'number') {
            const weight = param.weight ?? 1;
            return value * weight;
        }
        if (param.type === 'enum') {
            return value;
        }
    };

    const getStatus = () => {
        if (chartData.length === 0) return param.status;
        const value = chartData[chartData.length - 1].value;
        const weight = param.weight ?? 1;
        if (
            param.error_high == null &&
            param.error_low == null &&
            param.warn_high == null &&
            param.warn_low == null
        ) {
            return null;
        }
        var status = 0;
        if (param.warn_high != null && value >= param.warn_high * weight) {
            status = 100;
        }
        if (param.warn_low != null && value <= param.warn_low * weight) {
            status = 100;
        }
        if (param.error_high != null && value >= param.error_high * weight) {
            status = 200;
        }
        if (param.error_low != null && value <= param.error_low * weight) {
            status = 200;
        }
        return status;
    };

    return (
        <Card sx={{ minHeight: '300px' }}>
            <CardContent>
                <Typography variant="h6" color={getColor()}>
                    {param.name} {param.um ? `(${param.um})` : ''}
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                    <LineChart
                        data={chartData}
                        margin={{
                            top: 10,
                            right: 10,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="ts"
                            style={{ fontSize: 10 }}
                            tick={{
                                transform: 'translate(0, 5)',
                            }}
                            type="number"
                            domain={[
                                () => {
                                    return new Date().getTime() - delta * 1000;
                                },
                                () => {
                                    return new Date().getTime();
                                },
                            ]}
                            tickFormatter={(value) => {
                                return moment(new Date(value)).format(
                                    'DD/MM/YYYY HH:mm:ss'
                                );
                            }}
                            allowDataOverflow={true}
                        />
                        {param.type === 'number' && (
                            <YAxis
                                style={{ fontSize: 10 }}
                                tick={{ transform: 'translate(-5, 0)' }}
                            />
                        )}
                        {param.type === 'enum' && (
                            <YAxis
                                style={{ fontSize: 10 }}
                                tick={{ transform: 'translate(-5, 0)' }}
                                ticks={Object.keys(param.values!)}
                                tickFormatter={(value) => {
                                    return param.values![value][0];
                                }}
                            />
                        )}
                        <Tooltip
                            labelFormatter={(value) => {
                                return moment(new Date(value)).format(
                                    'DD/MM/YYYY HH:mm:ss'
                                );
                            }}
                            formatter={(value) => {
                                if (param.type === 'number') {
                                    return `${value} ${param.um ?? ''}`;
                                }
                                if (param.type === 'enum') {
                                    if (
                                        param.values![value.toString()] &&
                                        param.values![value.toString()][0]
                                    ) {
                                        return param.values![
                                            value.toString()
                                        ][0];
                                    } else {
                                        return value;
                                    }
                                }
                                return value;
                            }}
                        />
                        <Line
                            isAnimationActive={false}
                            type="stepAfter"
                            dataKey="value"
                            stroke={theme.palette.primary.main}
                            activeDot={{ r: 8 }}
                        />
                        {param.warn_high && (
                            <ReferenceLine
                                y={getFinalValue(param.warn_high)}
                                stroke={SEVERITY_COLORS['100']}
                            />
                        )}
                        {param.warn_low && (
                            <ReferenceLine
                                y={getFinalValue(param.warn_low)}
                                stroke={SEVERITY_COLORS['100']}
                            />
                        )}
                        {param.error_high && (
                            <ReferenceLine
                                y={getFinalValue(param.error_high)}
                                stroke={SEVERITY_COLORS['200']}
                            />
                        )}
                        {param.error_low && (
                            <ReferenceLine
                                y={getFinalValue(param.error_low)}
                                stroke={SEVERITY_COLORS['200']}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default ParamChart;
